import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiVentasInstance, authHeaderVentas } from "../../redux/actions/ventas/ventas";

import { Form, Select, Button, Row, Col, Typography, Space, InputNumber, Switch, Dropdown, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { CheckOutlined, MoreOutlined } from "@ant-design/icons";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

const EMPTY_LINEA = () => ({
  key: Date.now() + Math.random(),
  tipo_linea: "producto",
  id_ref: null,
  descripcion: "",
  cantidad: "",
  precio_cobrado: "",
  confirmed: false,
});

const EMPTY_PAGO = () => ({
  key: Date.now() + Math.random(),
  id_forma_pago: null,
  monto: "",
});

const fmt = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CrearVentaPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [variantesOpts, setVariantesOpts] = useState([]);
  const [serviciosOpts, setServiciosOpts] = useState([]);

  const [lineas, setLineas] = useState([EMPTY_LINEA()]);
  const [pagos, setPagos] = useState([EMPTY_PAGO()]);
  const esCredito = Form.useWatch("es_credito", form);

  useEffect(() => {
    const headers = authHeaderVentas();
    apiVentasInstance.get("/clientes", { headers }).then((r) => setClientes((r.data?.items ?? []).map((c) => ({ label: c.nombre, value: c.id_cliente })))).catch(() => {});
    apiVentasInstance.get("/formas-pago", { headers }).then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago })))).catch(() => {});
    apiVentasInstance.get("/productos/variantes/buscar", { headers }).then((r) => {
      setVariantesOpts((r.data?.items ?? []).map((v) => ({
        label: `${v.producto_nombre} (${v.clave})`, value: v.id_variante,
        nombre: `${v.producto_nombre} (${v.clave})`, precio: parseFloat(v.precio_venta), existencia: parseFloat(v.existencia),
      })));
    }).catch(() => {});
    apiVentasInstance.get("/servicios", { headers }).then((r) => {
      setServiciosOpts((r.data?.items ?? []).map((s) => ({
        label: s.nombre, value: s.id_servicio, nombre: s.nombre, precio: s.precio_base != null ? parseFloat(s.precio_base) : 0,
      })));
    }).catch(() => {});
  }, []);

  const updateLinea = (index, field, value) => {
    setLineas((prev) => prev.map((l, i) => {
      if (i !== index) return l;
      if (field === "tipo_linea") return { ...l, tipo_linea: value, id_ref: null, descripcion: "", precio_cobrado: "" };
      if (field === "id_ref") {
        const opts = l.tipo_linea === "producto" ? variantesOpts : serviciosOpts;
        const opt = opts.find((o) => o.value === value);
        return { ...l, id_ref: value, descripcion: opt?.nombre || "", precio_cobrado: opt?.precio ?? "" };
      }
      return { ...l, [field]: value };
    }));
  };

  const confirmLinea = (index) => {
    const l = lineas[index];
    if (!l.id_ref) return toast.warning("Selecciona un producto o servicio");
    if (l.cantidad === "" || l.cantidad == null) return toast.warning("Ingresa la cantidad");
    if (l.precio_cobrado === "" || l.precio_cobrado == null) return toast.warning("Ingresa el precio");

    if (l.tipo_linea === "producto") {
      const opt = variantesOpts.find((o) => o.value === l.id_ref);
      if (opt && parseFloat(l.cantidad) > opt.existencia) {
        toast.error(`Solo hay ${opt.existencia} en existencia`);
        return;
      }
    }

    setLineas((prev) => {
      const updated = prev.map((item, i) => (i === index ? { ...item, confirmed: true } : item));
      const hasOpenRow = updated.some((item) => !item.confirmed);
      return hasOpenRow ? updated : [...updated, EMPTY_LINEA()];
    });
  };

  const editLinea = (index) => setLineas((prev) => prev.map((l, i) => (i === index ? { ...l, confirmed: false } : l)));
  const removeLinea = (index) => setLineas((prev) => {
    const next = prev.filter((_, i) => i !== index);
    const hasOpenRow = next.some((l) => !l.confirmed);
    return hasOpenRow || next.length === 0 ? next : [...next, EMPTY_LINEA()];
  });

  const addPago = () => setPagos((prev) => [...prev, EMPTY_PAGO()]);
  const updatePago = (index, field, value) => setPagos((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  const removePago = (index) => setPagos((prev) => prev.filter((_, i) => i !== index));

  const totalPreview = lineas.filter((l) => l.confirmed).reduce((a, l) => a + (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_cobrado) || 0), 0);
  const totalPagosPreview = pagos.reduce((a, p) => a + (parseFloat(p.monto) || 0), 0);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validLineas = lineas
      .filter((l) => l.confirmed && l.id_ref)
      .map((l) => ({
        tipo_linea: l.tipo_linea,
        id_variante: l.tipo_linea === "producto" ? l.id_ref : null,
        id_servicio: l.tipo_linea === "servicio" ? l.id_ref : null,
        descripcion: l.descripcion,
        cantidad: parseFloat(l.cantidad),
        precio_cobrado: parseFloat(l.precio_cobrado),
      }));

    if (validLineas.length === 0) {
      toast.error("Agrega al menos un producto o servicio");
      return;
    }

    const validPagos = pagos.filter((p) => p.id_forma_pago && p.monto !== "").map((p) => ({ id_forma_pago: p.id_forma_pago, monto: parseFloat(p.monto) }));

    const payload = {
      id_cliente: values.id_cliente || null,
      es_credito: Boolean(values.es_credito),
      nota_domicilio: values.nota_domicilio?.trim() || null,
      detalle: validLineas,
      pagos: validPagos,
    };

    setSaving(true);
    try {
      const { data } = await apiVentasInstance.post("/ventas", payload, { headers: authHeaderVentas() });
      toast.success("Venta registrada exitosamente");
      navigate(`/app/ventas/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>Nueva venta</Title>
            <Text className="eventos-subtitle">Registra una venta a un cliente. La existencia se descuenta automaticamente.</Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/ventas")} disabled={saving}>Cancelar</Button>
            <Button type="primary" loading={saving} icon={<i className="fa-solid fa-plus" />} onClick={handleSave} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
              Crear venta
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" initialValues={{ es_credito: false }}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-receipt"></i></span>
              Datos de la venta
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="id_cliente" label={<span className="paq-field-label">Cliente (opcional, publico general si se deja vacio)</span>}>
                  <Select placeholder="Publico general" options={clientes} showSearch optionFilterProp="label" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="es_credito" label={<span className="paq-field-label">Venta a credito</span>} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="nota_domicilio" label={<span className="paq-field-label">Nota (opcional)</span>}>
                  <Input.TextArea rows={2} placeholder="Notas adicionales sobre la venta..." autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="cc-section-card" style={{ marginTop: 20, paddingBottom: 20 }}>
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-list" /></span>
              Productos y servicios
            </div>

            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1050 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6, padding: "0 8px" }}>
                  <div style={{ width: 110, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Tipo</div>
                  <div style={{ flex: 1, minWidth: 220, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Producto / servicio</div>
                  <div style={{ width: 120, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Cantidad</div>
                  <div style={{ width: 140, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Precio</div>
                  <div style={{ width: 140, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Importe</div>
                  <div style={{ width: 130 }} />
                </div>

                {lineas.map((l, index) =>
                  l.confirmed ? (
                    <div key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", marginBottom: 6, background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                      <div style={{ width: 110, fontSize: 13, color: "#374151", textTransform: "capitalize" }}>{l.tipo_linea}</div>
                      <div style={{ flex: 1, minWidth: 220, fontSize: 13, fontWeight: 500, color: "#1f2937" }}>{l.descripcion}</div>
                      <div style={{ width: 120, fontSize: 13, color: "#374151" }}>{l.cantidad}</div>
                      <div style={{ width: 140, fontSize: 13, color: "#374151" }}>{fmt(l.precio_cobrado)}</div>
                      <div style={{ width: 140, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>{fmt((parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_cobrado) || 0))}</div>
                      <div style={{ width: 130, display: "flex", justifyContent: "flex-end" }}>
                        <Dropdown
                          menu={{
                            items: [
                              { key: "edit", label: "Editar", icon: <i className="fa-solid fa-pen" /> },
                              { key: "delete", label: "Eliminar", icon: <i className="fa-solid fa-trash" />, danger: true },
                            ],
                            onClick: ({ key }) => (key === "edit" ? editLinea(index) : removeLinea(index)),
                          }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <button type="button" style={{ width: 32, height: 32, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MoreOutlined style={{ fontSize: 16, color: "#6b7280" }} />
                          </button>
                        </Dropdown>
                      </div>
                    </div>
                  ) : (
                    <div key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, padding: "8px", background: "#fffdf5", borderRadius: 8, border: "1px dashed #d1d5db" }}>
                      <Select
                        style={{ width: 110 }}
                        options={[{ label: "Producto", value: "producto" }, { label: "Servicio", value: "servicio" }]}
                        value={l.tipo_linea}
                        onChange={(v) => updateLinea(index, "tipo_linea", v)}
                      />
                      <Select
                        style={{ flex: 1, minWidth: 220 }}
                        placeholder={l.tipo_linea === "producto" ? "Selecciona un producto" : "Selecciona un servicio"}
                        options={l.tipo_linea === "producto" ? variantesOpts : serviciosOpts}
                        value={l.id_ref || undefined}
                        onChange={(val) => updateLinea(index, "id_ref", val)}
                        showSearch
                        optionFilterProp="label"
                      />
                      <InputNumber style={{ width: 120 }} placeholder="Cantidad" min={0} precision={l.tipo_linea === "producto" ? 3 : 0} value={l.cantidad !== "" ? l.cantidad : undefined} onChange={(v) => updateLinea(index, "cantidad", v ?? "")} />
                      <InputNumber style={{ width: 140 }} placeholder="Precio" min={0} precision={2} value={l.precio_cobrado !== "" ? l.precio_cobrado : undefined} onChange={(v) => updateLinea(index, "precio_cobrado", v ?? "")} />
                      <div style={{ width: 130, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <Button icon={<CheckOutlined />} onClick={() => confirmLinea(index)} className="eventos-btn-clean" style={{ width: 90 }}>Aceptar</Button>
                        <button type="button" className="paq-contenido-delete-btn" onClick={() => removeLinea(index)} title="Eliminar">
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            <div style={{ textAlign: "right", marginTop: 12, fontSize: 15, fontWeight: 700, color: "#3d0011" }}>
              Total: {fmt(totalPreview)}
            </div>
          </div>

          {!esCredito && (
            <div className="cc-section-card" style={{ marginTop: 20, paddingBottom: 20 }}>
              <div className="cc-section-header">
                <span className="cc-section-icon"><i className="fa-solid fa-money-bill-wave" /></span>
                Pagos
              </div>

              {pagos.map((p, index) => (
                <div key={p.key} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <Select style={{ width: 200 }} placeholder="Forma de pago" options={formasPago} value={p.id_forma_pago || undefined} onChange={(v) => updatePago(index, "id_forma_pago", v)} />
                  <InputNumber style={{ width: 160 }} placeholder="Monto" min={0} precision={2} value={p.monto !== "" ? p.monto : undefined} onChange={(v) => updatePago(index, "monto", v ?? "")} />
                  <button type="button" className="paq-contenido-delete-btn" onClick={() => removePago(index)}>
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              ))}
              <Button onClick={addPago} className="eventos-btn-clean">Agregar pago</Button>

              <div style={{ textAlign: "right", marginTop: 12, fontSize: 13, color: totalPagosPreview >= totalPreview ? "#15803d" : "#b45309" }}>
                Pagado: {fmt(totalPagosPreview)} de {fmt(totalPreview)}
              </div>
            </div>
          )}
        </Form>
      </div>
    </main>
  );
}
